// @ts-check

/// <reference types="vite/client" />

import * as vue from "vue";
import { registerCustomElement } from "./registration";
import { enrichProps } from "./utils";

/**
 * @typedef {{ component: import("vue").DefineComponent, plugins: import("vue").Plugin[] }} AppSetup
 * @typedef { (props: import("vue").ComponentObjectPropsOptions) => AppSetup } AppSetupFunction
 * @typedef {{ setup: AppSetupFunction }} App
 */

/**
 * Registers all components in the apps directory.
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 */
export async function registerAppComponents(props) {
  /**
   * @type {Record<string, App>}
   */
  const apps = import.meta.glob("../apps/**/main.js", { eager: true });

  for (const [path, app] of Object.entries(apps)) {
    const fileName = path.split("/").at(-2);

    if (!fileName) {
      console.warn(`Invalid app file name: ${path}`);
      continue;
    }

    const elementConstructor = defineAppCustomElement(app, fileName, props);
    registerCustomElement(fileName, elementConstructor);
  }
}

/**
 * Defines a custom element for compatibility.
 * These custom elements are used to wrap Vue 3 components in a custom element.
 * That way we can use the full power of Vue 3 components, including a store etc. in a custom element.
 *
 * @param {App} main - The main object of the app. It must contain a setup function that returns the component and plugins.
 * @param {string} fileName - The file name of the app.
 * @param {import("vue").ComponentObjectPropsOptions} props - Default values for the component props.
 * @property {string} embedHost - The embed host.
 *
 * @returns {import("vue").VueElementConstructor<{}>}
 */
function defineAppCustomElement(main, fileName, props) {
  const { component, plugins } = main.setup(props);

  const enrichedProps = enrichProps(component, fileName, props);

  const setup = () => {
    const app = vue.createApp(component);

    // If plugins are defined, use them.
    plugins?.forEach((plugin) => app.use(plugin));

    // re-assign required properties to the current instance, they might be used by the component plugins
    const inst = vue.getCurrentInstance();

    if (inst) {
      Object.assign(inst.appContext, app._context);
      Object.assign(inst["provides"], app._context.provides);
    }
  };

  /**
   * @type {string[]}
   */
  // @ts-ignore
  const emits = component.emits ?? [];

  const render = ({ $props, $emit }) => {
    const domEventName = (eventName) => `on${eventName[0].toUpperCase()}${eventName.slice(1)}`;
    const events = Object.fromEntries(
      emits.map((eventName) => [domEventName(eventName), (payload) => $emit(eventName, payload)]),
    );

    return vue.h(component, {
      ...$props,
      ...events,
    });
  };

  // @ts-ignore
  return vue.defineCustomElement({
    ...component,
    props: enrichedProps,
    setup,
    render,
  });
}
